import type { ReactNode } from 'react'
import { createContext, useContext, useState } from 'react'
import { createPortal } from 'react-dom'

import { CloseOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import { Button, Typography } from 'antd'
import cx from 'classnames'

const { Title } = Typography

export interface HelpSection {
  title?: string
  content: ReactNode
  dividerAfter?: boolean
}

interface HelpDrawerContextValue {
  open: boolean
  toggle: () => void
}

const HelpDrawerContext = createContext<HelpDrawerContextValue | null>(null)

export interface HelpDrawerLayoutProps {
  title: string
  sections: HelpSection[]
  children: ReactNode
}

export function HelpDrawerLayout({
  title,
  sections,
  children
}: HelpDrawerLayoutProps): JSX.Element {
  const [open, setOpen] = useState(false)

  return (
    <HelpDrawerContext.Provider value={{ open, toggle: () => setOpen(o => !o) }}>
      <div
        className={cx('transition-[margin] duration-300 ease-in-out', open ? 'mr-[400px]' : 'mr-0')}
      >
        {children}
      </div>
      {createPortal(
        <aside
          className={cx(
            'fixed top-0 right-0 h-screen w-[400px] bg-white border-l border-slate-200 overflow-y-auto transition-transform duration-300 ease-in-out z-[100]',
            open ? 'translate-x-0' : 'translate-x-full'
          )}
        >
          <div className='px-6 py-5'>
            <div className='flex justify-between items-center mb-4'>
              <Title level={4} className='!mb-0'>
                {title}
              </Title>
              <Button
                type='text'
                size='small'
                icon={<CloseOutlined />}
                onClick={() => setOpen(false)}
                className='!text-slate-400 hover:!text-slate-600'
              />
            </div>
            {sections.map((section, index) => (
              <div key={section.title ?? index} className={index > 0 ? 'mt-5 pt-5' : ''}>
                {section.title ? (
                  <div className='mb-1.5 text-xs font-medium text-slate-500'>{section.title}</div>
                ) : null}
                <div className='text-sm leading-relaxed text-slate-600'>{section.content}</div>
                {section.dividerAfter ? <div className='mt-5 border-b border-slate-200' /> : null}
              </div>
            ))}
          </div>
        </aside>,
        document.body
      )}
    </HelpDrawerContext.Provider>
  )
}

export function HelpDrawerTrigger(): JSX.Element | null {
  const ctx = useContext(HelpDrawerContext)
  if (!ctx) return null

  return (
    <Button
      type='default'
      size='middle'
      icon={<QuestionCircleOutlined />}
      onClick={ctx.toggle}
      className='!text-slate-400 hover:!text-blue-500 !bg-slate-50 !border-slate-200 !rounded-lg'
      aria-label='Page documentation'
    />
  )
}
